import { handleAxiosDates } from '@shared/utils/dateUtil';
import {
  LOGIN_DEVICE_ID_HEADER,
  getLoginDeviceId,
  isLoginEndpoint,
} from '@shared/utils/loginDevice.util';
import { NotificationInstance } from 'antd/es/notification/interface';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

// // console.log('process.env.REACT_APP_BASE_URL', process.env.REACT_APP_BASE_URL);
export const api_ = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

//* 강제 로그아웃 후 로그인 페이지에서 안내 메시지를 띄우기 위한 플래그 (App.tsx에서 소비)
export const FORCED_LOGOUT_NOTICE_KEY = 'forcedLogoutNotice';

//* 인터셉터는 React 밖이라 recoil의 notification 인스턴스를 못 쓴다.
//* App.tsx가 인스턴스를 만들 때 여기로도 꽂아준다 (권한 부족 401 안내용).
let axiosNotifier: NotificationInstance | null = null;
export const setAxiosNotifier = (instance: NotificationInstance | null) => {
  axiosNotifier = instance;
};

// 로그인/회원가입류 엔드포인트의 401은 "비밀번호 오류"라 세션 무효와 무관 (verifyCurrentPassword 포함)
const isCredentialEndpoint = (url?: string) =>
  !!url && (url.includes('auth/login') || url.includes('auth/signup'));

let forcedLogoutInProgress = false;

/**
 * 토큰 실린 401은 원인이 둘인데, 상태코드가 같아 구분이 필요하다.
 *
 * ① 유령 로그인 (강제 로그아웃 대상 — 이 기능의 원래 목적):
 *    local 작업 중 localStorage에 "다른 서버용 토큰"(형태는 비슷하지만 이 서버에선
 *    invalid signature)이 남은 경우. 프론트 라우트 가드는 userProfile만 보고
 *    통과시키는데 API는 전부 401이 나는 유령 로그인 상태가 된다. 만료·유저 삭제도
 *    같은 부류(토큰 자체가 이 서버에서 무효).
 *
 * ② 권한 부족 (로그아웃 금지):
 *    백엔드 RolesGuard가 "인증은 됐지만 role 부족"에 403이 아닌 401을 던진다
 *    (roles.guard.ts의 UnauthorizedException). 토큰은 멀쩡하므로 그 기능 하나의
 *    에러로만 끝나야 한다. (GiftAgent가 admin 엔드포인트 호출 시 콘솔에서 통째로
 *    튕기던 사고의 원인)
 *
 * 판별: role 제한이 없는 auth/me(LocalAuthGuard만)에 같은 토큰을 찔러본다.
 * - auth/me 401 → ① 토큰 무효 → true (강제 로그아웃 진행)
 * - auth/me 200 → ② 권한 부족이었던 것 → false (알림만, 화면은 그대로)
 * - 네트워크 오류 등 → 판정 불가, 보수적으로 false (로그아웃하지 않음)
 * 인터셉터를 다시 타지 않도록 bare axios를 쓰고, 같은 토큰의 동시 다발 401은
 * in-flight 프로브 1회를 공유한다. (api_=localStorage / api_session=sessionStorage처럼
 * 서로 다른 토큰이 동시에 401날 수 있어 토큰별로 나눠 든다 — 한 토큰의 판정을
 * 다른 토큰에 적용하는 오판 방지)
 */
const tokenProbes = new Map<string, Promise<boolean>>();

const isTokenInvalidOnThisServer = (authorization: string): Promise<boolean> => {
  const inFlight = tokenProbes.get(authorization);
  if (inFlight) return inFlight;
  const probe = axios
    .get('auth/me', {
      baseURL: process.env.REACT_APP_BASE_URL,
      headers: { Authorization: authorization },
    })
    .then(() => false)
    .catch((probeError) => (probeError as AxiosError)?.response?.status === 401)
    .finally(() => {
      tokenProbes.delete(authorization);
    });
  tokenProbes.set(authorization, probe);
  return probe;
};

//* 한 페이지의 병렬 쿼리가 동시에 권한 401을 물어와도 알림은 1회만
let lastPermissionDeniedAt = 0;
const PERMISSION_DENIED_THROTTLE_MS = 2500;

/**
 * 권한 부족 401(위 ②)의 사용자 안내. **알림만 띄우고 화면은 건드리지 않는다.**
 *
 * 예전엔 GET이면 "그 화면을 볼 권한이 없는 것"으로 보고 이전 화면으로 되돌렸는데,
 * 401이 나는 GET은 페이지 진입 조회만이 아니다 — 폴링(useRecoilQueryInterVal),
 * 곁다리 위젯 조회처럼 화면과 무관한 요청도 섞여 있어서, 보고 있던 화면에서
 * 갑자기 뒤로 튕기고(스로틀이 풀리면 반복) 히스토리가 계속 밀리는 문제가 있었다.
 * 화면 접근 제어는 각 포털의 라우트 가드가 할 일이고, 여기서는 실패 사실만 알린다.
 */
const notifyPermissionDenied = (config?: AxiosRequestConfig) => {
  const now = Date.now();
  if (now - lastPermissionDeniedAt < PERMISSION_DENIED_THROTTLE_MS) return;
  lastPermissionDeniedAt = now;

  const isPageFetch = (config?.method ?? 'get').toLowerCase() === 'get';

  axiosNotifier?.error({
    key: 'permission-denied-401',
    message: '접근 권한이 없습니다',
    description: isPageFetch
      ? '현재 계정으로는 볼 수 없는 정보라 일부 내용이 표시되지 않습니다.'
      : '현재 계정으로는 사용할 수 없는 기능입니다.',
    placement: 'top',
    duration: 3,
  });
};

/**
 * 401 후처리 라우팅.
 * - 유령 로그인(위 ①)만 강제 로그아웃: 인증정보를 비우고 리로드하면
 *   각 포털의 라우트 가드가 자기 로그인 페이지로 보낸다.
 *   이때 "실패 요청에 실렸던 토큰"이 지금 저장소에 남아 있을 때만 지운다 —
 *   프로브 사이 재로그인·대리주문 세션 교체로 새 토큰이 들어왔다면 건드리지 않고,
 *   지울 때도 그 토큰이 든 저장소만 지운다(localStorage/sessionStorage 교차 삭제 금지).
 * - 권한 부족(위 ②)은 notifyPermissionDenied로 안내만.
 * 서버가 401을 응답한 경우에만 동작: 타임아웃/네트워크 단절(response 없음), 404/403 등은 건드리지 않는다.
 * 토큰 없이 받은 401(게스트)도 제외 — 로그아웃 후 재시도가 무한 리로드로 이어지지 않게 하는 안전장치.
 */
const forceLogoutOnInvalidSession = async (error: AxiosError) => {
  const config = error?.config;
  const headers = config?.headers as any;
  const sentAuthorization =
    headers?.Authorization ?? headers?.authorization ?? headers?.get?.('Authorization');
  if (
    error?.response?.status === 401 &&
    sentAuthorization &&
    !isCredentialEndpoint(config?.url) &&
    !forcedLogoutInProgress
  ) {
    const ghostSession = await isTokenInvalidOnThisServer(sentAuthorization as string);
    // 프로브를 기다리는 동안 다른 요청이 이미 로그아웃을 시작했을 수 있어 재확인
    if (ghostSession && !forcedLogoutInProgress) {
      const rawToken = String(sentAuthorization).replace(/^Bearer\s+/i, '');
      const isCurrentLocalToken = localStorage.getItem('token') === rawToken;
      const isCurrentSessionToken = sessionStorage.getItem('token') === rawToken;
      // 낡은 토큰(이미 세션 교체됨)의 뒤늦은 401 → 새 세션을 지우면 안 된다
      if (isCurrentLocalToken || isCurrentSessionToken) {
        forcedLogoutInProgress = true;
        if (isCurrentLocalToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('userProfile');
          localStorage.removeItem('userBugoBrand');
        }
        if (isCurrentSessionToken) {
          sessionStorage.removeItem('token');
        }
        sessionStorage.setItem(FORCED_LOGOUT_NOTICE_KEY, '1');
        window.location.reload();
      }
    } else if (!ghostSession) {
      notifyPermissionDenied(config);
    }
  }
  return Promise.reject(error);
};

/**
 * 운영 계정(Admin) 2단계 인증 — 로그인 요청에 이 브라우저의 기기 ID를 싣는다.
 * 서버는 이 값으로 "한 번 인증한 PC"를 30일간 기억하고, 같은 값 단위로 로그인 실패 잠금을 건다.
 * 헤더가 없으면 인증은 되지만 기억되지 않고, **잠금도 적용되지 않는다** — 그래서 고객 화면에서는
 * 일부러 싣지 않는다(loginDevice.util 참고). (구서버는 이 헤더를 무시한다)
 */
const attachLoginDeviceId = (config: AxiosRequestConfig) => {
  if (!config.headers || !isLoginEndpoint(config.url)) return;
  //* 고객 화면(스토어 로그인 등)의 호출은 헤더를 빼서 서버가 잠금을 건너뛰게 한다
  if (config.skipLoginDeviceId) return;
  const deviceId = getLoginDeviceId();
  if (deviceId) config.headers[LOGIN_DEVICE_ID_HEADER] = deviceId;
};

api_.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  attachLoginDeviceId(config);
  return config;
});

api_.interceptors.response.use((originalResponse) => {
  handleAxiosDates(originalResponse.data);
  return originalResponse;
}, forceLogoutOnInvalidSession);

export const api_session = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

api_session.interceptors.request.use((config: AxiosRequestConfig) => {
  const sessionToken = sessionStorage.getItem('token');
  if (sessionToken && config.headers)
    config.headers.Authorization = `Bearer ${sessionToken}`;
  attachLoginDeviceId(config);
  return config;
});

api_session.interceptors.response.use((originalResponse) => {
  handleAxiosDates(originalResponse.data);
  return originalResponse;
}, forceLogoutOnInvalidSession);
